import React from 'react';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import { InView } from 'react-intersection-observer';
import MenuItemCard from 'components/Menu/MenuItemCard';
import { mediaQueries, PHONE } from 'styles/mediaQueries';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';

import isCateringRoute from 'catering/helpers/isCateringRoute';

const InterSectionObserver = styled('div', {
  shouldForwardProp: muiPropForwarding(
    'hasImage',
    'hasDescription',
    'hasLongName',
    'hasLongDescription'
  )
})(({ hasImage, hasDescription, hasLongName, hasLongDescription }) => {
  /*
    the reason we have this elaborate min height classification
    is on mobile with the intersection observer, when a user navigates
    to menu item and then back to menu, we navigate the user to the same
    y-position they were previously on the menu. But since we render the menu
    again, the intersectional observers haven't been triggered yet. This means
    that without relatively accurate min heights set on these dom elements, the
    browser will scroll to a position y from previous location, but the menu items
    will begin loading during the scroll and change the y position of the browser,
    so that the user loses their place vertically.
  */
  let mobileMinHeight;

  if (hasImage && hasLongDescription && hasLongName) {
    mobileMinHeight = '317px';
  } else if ((hasImage && hasLongDescription) || (hasImage && hasLongName)) {
    mobileMinHeight = '299px';
  } else if (hasImage && hasDescription) {
    mobileMinHeight = '278px';
  } else if (hasImage && !hasDescription) {
    mobileMinHeight = '251px';
  } else {
    mobileMinHeight = '105px';
  }

  return {
    width: '100%',
    height: '100%',
    minHeight: '172px',
    [`@media ${mediaQueries[PHONE]}`]: {
      minHeight: mobileMinHeight
    }
  };
});

const LONG_NAME_LENGTH = 40;
const LONG_DESC_LENGTH = 50;

class MenuItem extends React.PureComponent {
  static propTypes = {
    handleClick: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    images: PropTypes.array,
    default_calories: PropTypes.number,
    default_price: PropTypes.string.isRequired,
    inventory: PropTypes.number,
    inventoryLessCart: PropTypes.number,
    lowInventoryCount: PropTypes.number,
    shouldLazyLoadMenu: PropTypes.bool,
    leadTimeText: PropTypes.string,
    minimum_quantity: PropTypes.number,
    quantity_handling_method: PropTypes.string,
    variants: PropTypes.array.isRequired,
    hasInvestorDayFeature: PropTypes.bool.isRequired,
    productHasSurchargeModifier: PropTypes.bool,
    // DIET TYPE OPTIONS
    contains_sugar: PropTypes.bool,
    dairy_free_option: PropTypes.bool,
    halal: PropTypes.bool,
    keto: PropTypes.bool,
    keto_option: PropTypes.bool,
    kosher: PropTypes.bool,
    kosher_option: PropTypes.bool,
    low_fodmap: PropTypes.bool,
    low_sodium: PropTypes.bool,
    non_alcoholic: PropTypes.bool,
    organic: PropTypes.bool,
    organic_option: PropTypes.bool,
    paleo: PropTypes.bool,
    paleo_option: PropTypes.bool,
    plant_based: PropTypes.bool,
    plant_based_option: PropTypes.bool,
    spicy: PropTypes.bool,
    spicy_hot: PropTypes.bool,
    spicy_medium: PropTypes.bool,
    spicy_mild: PropTypes.bool,
    sugar_free: PropTypes.bool,
    uncooked: PropTypes.bool,
    vegan: PropTypes.bool,
    vegan_option: PropTypes.bool,
    vegetarian: PropTypes.bool,
    vegetarian_option: PropTypes.bool,
    // ALLERGEN OPTIONS
    cooked_to_order: PropTypes.bool,
    corn: PropTypes.bool,
    corn_free: PropTypes.bool,
    dairy: PropTypes.bool,
    dairy_free: PropTypes.bool,
    egg: PropTypes.bool,
    egg_free: PropTypes.bool,
    fish: PropTypes.bool,
    fish_free: PropTypes.bool,
    gluten: PropTypes.bool,
    gluten_free: PropTypes.bool,
    nuts: PropTypes.bool,
    nuts_free: PropTypes.bool,
    peanut: PropTypes.bool,
    peanut_free: PropTypes.bool,
    raw: PropTypes.bool,
    raw_egg: PropTypes.bool,
    sesame: PropTypes.bool,
    sesame_free: PropTypes.bool,
    shellfish: PropTypes.bool,
    shellfish_free: PropTypes.bool,
    soy: PropTypes.bool,
    soy_free: PropTypes.bool,
    tree_nut: PropTypes.bool,
    tree_nut_free: PropTypes.bool,
    undercooked: PropTypes.bool,
    wheat: PropTypes.bool,
    wheat_free: PropTypes.bool
  };

  static defaultProps = {
    handleClick: () => {},
    quantity_handling_method: '',
    hasInvestorDayFeature: false,
    productHasSurchargeModifier: false
  };

  render() {
    const {
      name,
      description,
      default_calories: defaultCalories,
      default_price: defaultPrice,
      handleClick,
      inventory,
      inventoryLessCart,
      shouldLazyLoadMenu,
      leadTimeText,
      minimum_quantity: minimumQuantity,
      quantity_handling_method: quantityHandlingMethod,
      variants,
      lowInventoryCount,
      hasInvestorDayFeature,
      productHasSurchargeModifier,
      images,
      // DIET TYPE OPTIONS
      contains_sugar,
      dairy_free_option,
      halal,
      keto,
      keto_option,
      kosher,
      kosher_option,
      low_fodmap,
      low_sodium,
      non_alcoholic,
      organic,
      organic_option,
      paleo,
      paleo_option,
      plant_based,
      plant_based_option,
      spicy,
      spicy_hot,
      spicy_medium,
      spicy_mild,
      sugar_free,
      uncooked,
      vegan,
      vegan_option,
      vegetarian,
      vegetarian_option,
      // ALLERGEN OPTIONS
      cooked_to_order,
      corn,
      corn_free,
      dairy,
      dairy_free,
      egg,
      egg_free,
      fish,
      fish_free,
      gluten,
      gluten_free,
      nuts,
      nuts_free,
      peanut,
      peanut_free,
      raw,
      raw_egg,
      sesame,
      sesame_free,
      shellfish,
      shellfish_free,
      soy,
      soy_free,
      tree_nut,
      tree_nut_free,
      undercooked,
      wheat,
      wheat_free
    } = this.props;

    const isCatering = isCateringRoute();
    const image = get(images, '[0]');
    const isSoldOut = inventoryLessCart === 0;
    // only disable click for online ordering products that are sold out
    const processedHandleClick =
      isSoldOut && !isCatering ? () => {} : handleClick;
    const rootChild = (
      <MenuItemCard
        name={name}
        description={description}
        calories={defaultCalories}
        price={defaultPrice}
        image={image}
        handleClick={processedHandleClick}
        inventory={inventory}
        inventoryLessCart={inventoryLessCart}
        lowInventoryCount={lowInventoryCount}
        leadTimeText={leadTimeText}
        minimumQuantity={minimumQuantity}
        quantityHandlingMethod={quantityHandlingMethod}
        variants={variants}
        hasInvestorDayFeature={hasInvestorDayFeature}
        productHasSurchargeModifier={productHasSurchargeModifier}
        // DIET TYPE OPTIONS
        contains_sugar={contains_sugar}
        dairy_free_option={dairy_free_option}
        halal={halal}
        keto={keto}
        keto_option={keto_option}
        kosher={kosher}
        kosher_option={kosher_option}
        low_fodmap={low_fodmap}
        low_sodium={low_sodium}
        non_alcoholic={non_alcoholic}
        organic={organic}
        organic_option={organic_option}
        paleo={paleo}
        paleo_option={paleo_option}
        plant_based={plant_based}
        plant_based_option={plant_based_option}
        spicy={spicy}
        spicy_hot={spicy_hot}
        spicy_medium={spicy_medium}
        spicy_mild={spicy_mild}
        sugar_free={sugar_free}
        uncooked={uncooked}
        vegan={vegan}
        vegan_option={vegan_option}
        vegetarian={vegetarian}
        vegetarian_option={vegetarian_option}
        // ALLERGEN OPTIONS
        cooked_to_order={cooked_to_order}
        corn={corn}
        corn_free={corn_free}
        dairy={dairy}
        dairy_free={dairy_free}
        egg={egg}
        egg_free={egg_free}
        fish={fish}
        fish_free={fish_free}
        gluten={gluten}
        gluten_free={gluten_free}
        nuts={nuts}
        nuts_free={nuts_free}
        peanut={peanut}
        peanut_free={peanut_free}
        raw={raw}
        raw_egg={raw_egg}
        sesame={sesame}
        sesame_free={sesame_free}
        shellfish={shellfish}
        shellfish_free={shellfish_free}
        soy={soy}
        soy_free={soy_free}
        tree_nut={tree_nut}
        tree_nut_free={tree_nut_free}
        undercooked={undercooked}
        wheat={wheat}
        wheat_free={wheat_free}
      />
    );

    let child = rootChild;
    if (shouldLazyLoadMenu) {
      child = (
        <InView triggerOnce>
          {({ inView, ref, entry }) => (
            <InterSectionObserver
              ref={ref}
              hasImage={!!image}
              hasDescription={!!description}
              hasLongDescription={
                !!description && description.length > LONG_DESC_LENGTH
              }
              hasLongName={name.length > LONG_NAME_LENGTH}
            >
              {inView && rootChild}
            </InterSectionObserver>
          )}
        </InView>
      );
    }

    return child;
  }
}

export default withRouter(MenuItem);
